import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase/client';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';

/**
 * Magic-link landing page. The Supabase client picks up the session
 * from the URL automatically (detectSessionInUrl: true), so all we
 * need to do is wait for the SIGNED_IN event and redirect.
 */
export function AuthCallback() {
  useDocumentTitle('Signing in…');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const next = searchParams.get('next') || '/';
  const [stuck, setStuck] = useState(false);

  useEffect(() => {
    // Subscribe first so we don't miss the event.
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN') {
        navigate(next, { replace: true });
      }
    });

    // If we already have a session by the time we mount (race-free path),
    // redirect immediately.
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) navigate(next, { replace: true });
    });

    // If neither path resolved within 6s, surface a manual recovery option.
    const timeout = window.setTimeout(() => setStuck(true), 6000);

    return () => {
      sub.subscription.unsubscribe();
      window.clearTimeout(timeout);
    };
  }, [navigate, next]);

  return (
    <section className="container-prose py-20 text-center">
      {!stuck ? (
        <>
          <h1 className="font-serif text-2xl text-ink-900 dark:text-ink-50 mb-4">
            Signing you in…
          </h1>
          <div className="max-w-xs mx-auto space-y-2">
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-3/4 mx-auto" />
          </div>
        </>
      ) : (
        <>
          <h1 className="font-serif text-2xl text-ink-900 dark:text-ink-50 mb-3">
            This is taking longer than usual.
          </h1>
          <p className="text-ink-500 dark:text-ink-400 mb-6">
            The link may have expired. Try requesting a new one.
          </p>
          <Button onClick={() => navigate('/login', { replace: true })}>
            Back to sign-in
          </Button>
        </>
      )}
    </section>
  );
}
